import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import styles from "./WidgetPreview.module.css";
import {BotType, WidgetPosition} from "../../../../../../../api/generated";
import {AppearanceTabSection} from "../../../../../../elements/appearanceTabSection/AppearanceTabSection";
import {CustomSwitchField} from "../../../../../../elements/customSwitchField/CustomSwitchField";
import {SelectDropdown} from "../../../../../../elements/select/SelectDropdown";
import {WIDGET_BASE_URL} from "../../../../../../../configs";
import {WidgetAppearanceLocaleVariantUI} from "../../widgetAppearanceUpdateSlice";
import {buildPreviewMessage, isWidgetInitRequest, PREVIEW_VIEW_OPTIONS, PreviewView, previewUrl} from "./widgetPreviewSettings";

interface WidgetPreviewProps {
    botType: BotType;
    settings: WidgetAppearanceLocaleVariantUI;
}

const STAGE_VIEWPORT_GAP_PX = 120;
const MIN_STAGE_HEIGHT_PX = 600;
const WIDGET_FRAME_WIDTH_PX = 414;
const WIDGET_FRAME_MAX_HEIGHT_PX = 1072;
const WIDGET_FRAME_INSET_PX = 12;
const WIDGET_TOP_GAP_PX = 12;
const EDGE_PUSH_OUT_PX = 4;
const PHONE_WIDTH_PX = 390;
const PHONE_HEIGHT_PX = 844;
const PHONE_BORDER_PX = 1;

const frameOffset = (offsetPx: number) => Math.max(0, (offsetPx || 0) + EDGE_PUSH_OUT_PX - WIDGET_FRAME_INSET_PX);
const stageHeightForViewport = () => Math.max(MIN_STAGE_HEIGHT_PX, window.innerHeight - STAGE_VIEWPORT_GAP_PX);

export const WidgetPreview: React.FC<WidgetPreviewProps> = ({botType, settings}) => {
    const frameRef = useRef<HTMLIFrameElement>(null);
    const [isWidgetReady, setIsWidgetReady] = useState(false);
    const [view, setView] = useState<PreviewView>("open");
    const [isMobile, setIsMobile] = useState(false);
    const [stageHeight, setStageHeight] = useState(stageHeightForViewport);

    const src = useMemo(() => previewUrl(WIDGET_BASE_URL), []);
    const widgetOrigin = useMemo(() => new URL(src).origin, [src]);

    useEffect(() => {
        const handleResize = () => setStageHeight(stageHeightForViewport());
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const sendPreview = useCallback(() => {
        frameRef.current?.contentWindow?.postMessage(buildPreviewMessage(settings, botType, view, isMobile), widgetOrigin);
    }, [settings, botType, view, isMobile, widgetOrigin]);

    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            if (event.source === frameRef.current?.contentWindow && isWidgetInitRequest(event.data)) {
                setIsWidgetReady(true);
                sendPreview();
            }
        };
        window.addEventListener("message", handleMessage);
        return () => window.removeEventListener("message", handleMessage);
    }, [sendPreview]);

    useEffect(() => {
        if (isWidgetReady) {
            sendPreview();
        }
    }, [isWidgetReady, sendPreview]);

    const isRight = settings.widgetPosition === WidgetPosition.Right;
    const bottomOffset = frameOffset(settings.verticalOffsetPx);
    const phoneScale = Math.min(1, stageHeight / (PHONE_HEIGHT_PX + 2 * PHONE_BORDER_PX));

    const frameStyle: React.CSSProperties = isMobile
        ? {width: PHONE_WIDTH_PX, height: PHONE_HEIGHT_PX, transform: `scale(${phoneScale})`}
        : {
            width: WIDGET_FRAME_WIDTH_PX,
            height: Math.min(WIDGET_FRAME_MAX_HEIGHT_PX, stageHeight - bottomOffset - WIDGET_TOP_GAP_PX),
            bottom: bottomOffset,
            [isRight ? "right" : "left"]: frameOffset(settings.horizontalOffsetPx)
        };

    const frame = <iframe
        ref={frameRef}
        src={src}
        title="Widget preview"
        data-testid="widget-preview-frame"
        className={isMobile ? styles.phoneFrame : styles.desktopFrame}
        style={frameStyle}
    />;

    return (
        <div className={styles.column}>
            <AppearanceTabSection
                title="Preview"
                rightElement={
                    <div className={styles.headerControls}>
                        <div className={styles.viewSwitcher}>
                            <span className={styles.headerLabel}>View</span>
                            <div className={styles.viewSelect}>
                                <SelectDropdown<PreviewView>
                                    title="View"
                                    value={view}
                                    options={PREVIEW_VIEW_OPTIONS}
                                    handleChange={setView}
                                    renderMenuOnTop
                                    compact
                                />
                            </div>
                        </div>
                        <CustomSwitchField
                            textOnTrue="Phone"
                            textOnFalse="Phone"
                            value={isMobile}
                            toggleValue={setIsMobile}
                        />
                    </div>
                }
            >
                <div className={styles.previewContainer}>
                    <div className={isMobile ? styles.phoneStage : styles.desktopStage} style={{height: stageHeight}}>
                        <div style={isMobile ? {
                            width: (PHONE_WIDTH_PX + 2 * PHONE_BORDER_PX) * phoneScale,
                            height: (PHONE_HEIGHT_PX + 2 * PHONE_BORDER_PX) * phoneScale
                        } : undefined}>
                            {frame}
                        </div>
                        {!isMobile && <>
                            <div className={styles.horizontalMarker} style={{bottom: settings.verticalOffsetPx || 0}}/>
                            <div className={styles.verticalMarker} style={{[isRight ? "right" : "left"]: settings.horizontalOffsetPx || 0}}/>
                        </>}
                    </div>
                </div>
            </AppearanceTabSection>
        </div>
    );
};
